import React from 'react';
import { formatDateTime } from "../../common/dateTime";
import Box from '@mui/joy/Box';
import Chip from '@mui/joy/Chip';
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Divider from "@mui/joy/Divider";
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import IconButton, {iconButtonClasses} from "@mui/joy/IconButton";
import CircularProgress from "@mui/joy/CircularProgress";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import Button from "@mui/joy/Button";
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import {NotificationService} from "../../common/notification";
import EditorDialog from "./EditorDialog";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {commissionRulesListApi, commissionRulesAddApi, commissionRulesUpdateApi, commissionRulesDeleteApi} from "./commissionRulesApi";
import {gamesListApi} from "../games/gamesApi";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { RequestResultValue} from "../../common/api/requestResult";
import {getPages} from "../../common/pages/pages";
import ConfirmDialog from "../../components/confirmDialog/ConfirmDialog";
import {CommissionRule} from "../../models/commissionRule";
import {Game} from "../../models/games";
import {formatRubles} from "../../common/money";

const emptyDate = new Date(1900, 1, 1);
const emptyRule = {
    id: 0,
    gameId: 0,
    minAmountExclusive: BigInt(0),
    maxAmountInclusive: BigInt(0),
    fixedRate: BigInt(-1),
    percentRate: -1,
    disabled: false,
    updatedBy: '',
    created: emptyDate,
    updated: emptyDate
};

export function CommissionRulesTable() {
    const [games, setGames] = React.useState<Game[]>([]);
    const [loadedGames, setLoadedGames] = React.useState(false);

    const [rules, setRules] = React.useState<CommissionRule[]>([]);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(0);
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [filterGame, setFilterGame] = React.useState<number>(-1);
    const [filterActive, setFilterActive] = React.useState<number>(-1);
    const [filtersOpen, setFiltersOpen] = React.useState(false);
    const [selectedRule, setSelectedRule] = React.useState<CommissionRule>(emptyRule);
    const [loading, setLoading] = React.useState(false);
    const id = React.useRef(0);

    React.useEffect(() => {
        loadGames().catch(console.error);
    }, [])

    React.useEffect(() => {
        if (!filtersOpen) {
            loadData().catch(console.error);
        }
    }, [page, filterGame, filterActive])

    const loadGames = async () => {
        try {
            let result = await gamesListApi();
            if (result.result) {
                setGames(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoadedGames(true)
        }
    }

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await commissionRulesListApi(filterGame, filterActive, page, 20);
            if (result.result) {
                const res = result.value!;
                setPageCount(Math.ceil(res.total / 20));
                setRules(res.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const edit = (setting: CommissionRule) => {
        setSelectedRule(setting);
        setEditorOpen(true);
    }

    const onSave = async (rule: CommissionRule): Promise<boolean> => {
        setLoading(true)
        try {
            let res: RequestResultValue<CommissionRule>;
            if (rule.id === 0) {
                res = await commissionRulesAddApi(rule.gameId || 0, rule.minAmountExclusive, BigInt(rule.maxAmountInclusive || 0),
                    BigInt(rule.fixedRate || -1), rule.percentRate || -1, rule.disabled);
            } else {
                res = await commissionRulesUpdateApi(rule.id, rule.gameId || 0, rule.minAmountExclusive,
                    BigInt(rule.maxAmountInclusive || 0), BigInt(rule.fixedRate || -1), rule.percentRate || -1, rule.disabled);
            }

            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return false;
            }
            await loadData()
            return true;
        } finally {
            setLoading(false)
        }
    }

    const remove = (rowId: number) => {
        id.current = rowId;
        setConfirmOpen(true)
    }

    const onDelete = async () => {
        setLoading(true)
        try {
            const res = await commissionRulesDeleteApi(id.current);
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return;
            }
            await loadData()
        } finally {
            id.current = 0;
            setLoading(false)
        }
    }

    const getGameName = (id?: number): string => {
        if (!id)
            return '-';
        const gms = games.filter(p => p.id === id);
        if (gms.length !== 1)
            return '-';
        const game = gms[0];
        return game.name
    }

    const renderFilters = () => {
        return (
            <React.Fragment>
                <FormControl size="sm">
                    <FormLabel>Статус</FormLabel>
                    <Select
                        size="sm"
                        placeholder="Активность"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={filterActive}
                        onChange={(_, value) => setFilterActive(value ?? -1)}
                    >
                        <Option value={-1}>Все</Option>
                        <Option value={0}>Выключен</Option>
                        <Option value={1}>Активный</Option>
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Игра</FormLabel>
                    <Select size="sm"
                            startDecorator={<VideogameAssetIcon/>}
                            placeholder="Игра"
                            value={filterGame}
                            onChange={(_, value) => setFilterGame(value ?? 0)}>
                        <Option key={-1} value={-1}>Все</Option>
                        <Option key={0} value={0}>(все игры)</Option>
                        {games.map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                    </Select>
                </FormControl>
            </React.Fragment>
        );
    }

    const applyFilter = async () => {
        setFiltersOpen(false)
        if (page !== 1) {
            setPage(1);
        }
        else {
            loadData();
        }
    }

    const getRateDescription = (percentRate?: number, fixedRate?: bigint): string => {
        const numberFormat = new Intl.NumberFormat();

        if (percentRate && fixedRate) {
            return `Max(${numberFormat.format(percentRate || 0)}% или ${formatRubles(fixedRate)})`;
        }

         if (percentRate) {
            return `${numberFormat.format(percentRate || 0)}%`;
        }

        return formatRubles(fixedRate);
    }

    return (
        <React.Fragment>

            <ConfirmDialog
                title="Удалить?"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={onDelete}
            >
                Вы уверены что хотите удалить правило начисления комиссии?
            </ConfirmDialog>

            {editorOpen &&
                <EditorDialog rule={selectedRule}
                              allGames={games}
                              onConfirm={onSave}
                              open={editorOpen}
                              setOpen={setEditorOpen}/>
            }

            {loading ?
                <CircularProgress/> :
                <>
                    <Box
                        className="add-tabletUp"
                        sx={{
                            borderRadius: 'sm',
                            py: 2,
                            display: {xs: 'none', sm: 'flex'},
                            flexWrap: 'wrap',
                            gap: 1.5,
                            '& > *': {
                                minWidth: {xs: '120px', md: '160px'},
                            },
                        }}
                    >
                        <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => edit(emptyRule)}
                        >
                            <AddIcon/>
                        </IconButton>
                    </Box>

                    <Sheet
                        className="SearchAndFilters-mobile"
                        sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
                    >
                        <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => setFiltersOpen(true)}
                        >
                            <FilterAltIcon/>
                        </IconButton>

                        <Modal open={filtersOpen} onClose={() => setFiltersOpen(false)}>
                            <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
                                <ModalClose/>
                                <Typography id="filter-modal" level="h2">
                                    Фильтры
                                </Typography>
                                <Divider sx={{my: 2}}/>
                                <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                    {renderFilters()}
                                    <Button color="primary" onClick={applyFilter}>
                                        Применить
                                    </Button>
                                    <Button color="neutral" onClick={() => setFiltersOpen(false)}>
                                        Закрыть
                                    </Button>
                                </Sheet>
                            </ModalDialog>
                        </Modal>
                    </Sheet>

                    <Box
                        className="SearchAndFilters-tabletUp"
                        sx={{
                            borderRadius: 'sm',
                            py: 2,
                            display: {xs: 'none', sm: 'flex'},
                            flexWrap: 'wrap',
                            gap: 1.5,
                            '& > *': {
                                minWidth: {xs: '120px', md: '160px'},
                            },
                        }}
                    >
                        {renderFilters()}
                    </Box>

                    <Sheet
                        className="OrderTableContainer"
                        variant="outlined"
                        sx={{
                            display: {xs: 'none', sm: 'initial'},
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            minHeight: 0,
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{width: 100, padding: '12px 6px'}}>#</th>
                                <th style={{width: 100, padding: '12px 6px'}}>Игра</th>
                                <th style={{width: 180, padding: '12px 6px'}}>Стоимость, ₽</th>
                                <th style={{width: 100, padding: '12px 6px'}}>Комиссия</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Создано</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Обновлено</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Пользователь</th>
                                <th style={{width: 100, padding: '12px 6px'}}>Активно</th>
                                <th style={{width: 100, padding: '12px 6px'}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {rules.map((row) => (
                                <tr key={row.id} style={row.disabled ? {backgroundColor: 'red'} : {}}>
                                    <td>
                                        <Typography level="body-xs">{row.id}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{getGameName(row.gameId)}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">
                                            {formatRubles(row.minAmountExclusive)} - {row.maxAmountInclusive?formatRubles(row.maxAmountInclusive):'..'}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">
                                            {getRateDescription(row.percentRate, row.fixedRate)}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{formatDateTime(row.created)}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{formatDateTime(row.updated)}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.updatedBy}</Typography>
                                    </td>
                                    <td>
                                        {row.disabled
                                        ?<Chip
                                            variant="soft"
                                            size="sm"
                                            startDecorator={<CancelIcon/>}
                                            color='danger'
                                        />
                                        :<Chip
                                            variant="soft"
                                            size="sm"
                                            startDecorator={<CheckCircleIcon/>}
                                            color='success'
                                        />}
                                    </td>
                                    <td>
                                        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                                            <IconButton
                                                size="sm"
                                                variant="outlined"
                                                color="neutral"
                                                onClick={() => edit(row)}
                                            >
                                                <EditIcon/>
                                            </IconButton>
                                            <IconButton
                                                size="sm"
                                                variant="outlined"
                                                color="neutral"
                                                onClick={() => remove(row.id)}
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        </Box>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Sheet>
                    <Box
                        className="Pagination-laptopUp"
                        sx={{
                            pt: 2,
                            gap: 1,
                            [`& .${iconButtonClasses.root}`]: {borderRadius: '50%'},
                            display: {
                                xs: 'none',
                                md: 'flex',
                            },
                        }}
                    >
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<KeyboardArrowLeftIcon/>}
                            disabled={page === 1}
                            onClick={() => setPage(state => state - 1)}
                        >
                            Назад
                        </Button>

                        <Box sx={{flex: 1}}/>
                        {getPages(pageCount).map((p) => (
                            <IconButton
                                key={p}
                                size="sm"
                                variant={Number(p) ? 'outlined' : 'plain'}
                                color={page === Number(p) ? "primary" : "neutral"}
                                onClick={() => setPage(Number(p))}
                            >
                                {p}
                            </IconButton>
                        ))}
                        <Box sx={{flex: 1}}/>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            endDecorator={<KeyboardArrowRightIcon/>}
                            disabled={page === pageCount}
                            onClick={() => setPage(state => state + 1)}
                        >
                            Дальше
                        </Button>
                    </Box>

                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => edit(emptyRule)}
                        >
                            <AddIcon/>
                        </IconButton>
                        {rules.map((listItem) => (
                            <List key={listItem.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                    style={listItem.disabled ? {backgroundColor: 'red'} : {}}
                                >
                                    <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <Stack spacing={1} direction={'row'}>
                                            <div>
                                                <Typography gutterBottom sx={{fontWeight: 600}}>
                                                    #{listItem.id}. {getGameName(listItem.gameId)}:
                                                </Typography>
                                                <Typography level="body-xs">
                                                    Стоимость скина: {formatRubles(listItem.minAmountExclusive)} - {listItem.maxAmountInclusive?formatRubles(listItem.maxAmountInclusive):'..'}
                                                </Typography>
                                                <Typography level="body-xs">
                                                    Комиссия: {getRateDescription(listItem.percentRate, listItem.fixedRate)}
                                                </Typography>
                                                <Typography level="body-xs">
                                                    Создано: {formatDateTime(listItem.created)}
                                                </Typography>
                                                <Typography level="body-xs">
                                                    Обновлено: {formatDateTime(listItem.updated)}
                                                </Typography>
                                                <Typography level="body-xs">
                                                    Пользователь: {listItem.updatedBy}
                                                </Typography>
                                                <Box
                                                    sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                    <Link level="body-sm" component="button"
                                                            onClick={() => edit(listItem)}>
                                                        Редактировать
                                                    </Link>
                                                    <Link level="body-sm" component="button"
                                                            onClick={() => remove(listItem.id)}>
                                                        Удалить
                                                    </Link>
                                                </Box>
                                            </div>
                                        </Stack>
                                    </ListItemContent>
                                    {listItem.disabled ?
                                        <Tooltip title='Выключено'>
                                            <Chip
                                                variant="soft"
                                                size="sm"
                                                startDecorator={<CancelIcon/>}
                                                color='danger'
                                            />
                                        </Tooltip>
                                        :<Tooltip title='Активно'>
                                            <Chip
                                                variant="soft"
                                                size="sm"
                                                startDecorator={<CheckCircleIcon/>}
                                                color='success'
                                            />
                                        </Tooltip>
                                    }
                                </ListItem>
                                <ListDivider/>
                            </List>
                        ))}
                    </Box>
                    <Box
                        className="Pagination-mobile"
                        sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', py: 2}}
                    >
                        <IconButton
                            aria-label="previous page"
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => setPage(state => state - 1)}
                        >
                            <KeyboardArrowLeftIcon/>
                        </IconButton>
                        <Typography level="body-sm" sx={{mx: 'auto'}}>
                            Страница {page} из {pageCount}
                        </Typography>
                        <IconButton
                            aria-label="next page"
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            disabled={page === pageCount}
                            onClick={() => setPage(state => state + 1)}
                        >
                            <KeyboardArrowRightIcon/>
                        </IconButton>
                    </Box>
                </>
            }
        </React.Fragment>
    );
}
